import React, { useEffect, useState } from "react";
import ToolShell from "@/components/ToolShell";
import CurrencyInput from "@/components/CurrencyInput";
import CollegeSelect from "@/components/CollegeSelect";
import ResidencyToggle from "@/components/ResidencyToggle";
import { track } from "@/lib/analytics";
import { Plus, Star, Trash2, School } from "lucide-react";

const MAX = 4;

const blank = () => ({ schoolName: "", residency: "", coa: "", grants: "", scholarships: "", loans: "", workStudy: "" });

function compute(s) {
  const coa = Number(s.coa) || 0;
  const grants = Number(s.grants) || 0;
  const scholarships = Number(s.scholarships) || 0;
  const loans = Number(s.loans) || 0;
  const netCost = Math.max(0, coa - grants - scholarships);
  const gift = grants + scholarships;
  const pctFree = coa > 0 ? Math.round((gift / coa) * 100) : 0;
  return { ...s, netCost, fourYear: netCost * 4, gift, loans, pctFree };
}

export default function ComparePage() {
  const [schools, setSchools] = useState([]);
  const [draft, setDraft] = useState(blank());

  useEffect(() => { track.tool_opened("compare"); }, []);

  const add = () => {
    if (!draft.schoolName || !draft.coa) return;
    const computed = compute({ ...draft, id: `s_${Date.now()}` });
    const next = [...schools, computed];
    setSchools(next);
    setDraft(blank());
    track.compare_school_added();
  };

  const remove = (id) => setSchools(s => s.filter(x => x.id !== id));

  const cheapest = schools.length ? Math.min(...schools.map(s => s.netCost)) : null;
  const schoolsRanked = schools.map(s => ({ ...s, isBest: s.netCost === cheapest && schools.length > 1 }));

  return (
    <ToolShell wide tool="Tool 05 · School Cost Comparer" title="Compare up to 4 aid offers, honestly." subtitle="Net cost + 4-year cost + grant-to-loan ratio. School lookup uses the U.S. Department of Education's College Scorecard. All math stays in your browser.">
      {/* Add form */}
      {schools.length < MAX && (
        <div className="card mb-6" data-testid="compare-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2 relative">
              <label className="block text-sm text-ink-muted mb-2">School name</label>
              <CollegeSelect
                value={draft.schoolName}
                onChange={(name) => setDraft(d => ({ ...d, schoolName: name }))}
                placeholder="Start typing a school…"
                inputClassName="input"
                testId="compare-school-name"
              />
            </div>
            <div className="sm:col-span-2">
              <ResidencyToggle
                value={draft.residency}
                onChange={(v) => setDraft(d => ({ ...d, residency: v }))}
                testId="compare-residency"
              />
            </div>
            {[
              ["coa", "Cost of attendance"],
              ["grants", "Grants"],
              ["scholarships", "Scholarships"],
              ["loans", "Loans offered"],
              ["workStudy", "Work-study"],
            ].map(([k, label]) => (
              <CurrencyInput
                key={k}
                id={`compare-${k}`}
                label={label}
                value={draft[k]}
                onChange={(v) => setDraft({ ...draft, [k]: v })}
                placeholder="0"
                testid={`compare-input-${k}`}
              />
            ))}
          </div>
          <button onClick={add} disabled={!draft.schoolName || !draft.residency || !draft.coa} className="btn btn-primary mt-5" data-testid="compare-add">
            <Plus className="w-4 h-4" /> Add school
          </button>
          {!draft.residency && draft.schoolName && (
            <p className="text-xs text-amber mt-2" data-testid="compare-residency-required">
              Pick In-State or Out-of-State for {draft.schoolName}.
            </p>
          )}
        </div>
      )}

      {/* Comparison cards */}
      {schools.length > 0 && (
        <div className="grid md:grid-cols-2 gap-5" data-testid="compare-cards">
          {schoolsRanked.map(s => (
            <div key={s.id} className={`card relative ${s.isBest ? "ring-2 ring-success" : ""}`} data-testid={`compare-card-${s.id}`}>
              {s.isBest && (
                <div className="absolute top-4 right-4 flex items-center gap-1 text-xs uppercase tracking-widest text-success">
                  <Star className="w-3 h-3 fill-current" /> Best value
                </div>
              )}
              <div className="flex items-start gap-3 mb-4">
                <School className="w-5 h-5 text-accent mt-0.5" />
                <div className="pr-20">
                  <h3 className="font-heading text-2xl text-ink">{s.schoolName}</h3>
                  {s.residency && (
                    <div className="text-xs text-ink-dim mt-0.5" data-testid={`compare-card-residency-${s.id}`}>
                      {s.residency === "in_state" ? "In-State" : "Out-of-State"}
                    </div>
                  )}
                </div>
              </div>
              <dl className="space-y-1 text-sm">
                <Row label="Cost of attendance" value={`$${s.coa.toLocaleString()}`} />
                <Row label="Grants" value={`−$${Number(s.grants).toLocaleString()}`} className="text-success" />
                <Row label="Scholarships" value={`−$${Number(s.scholarships).toLocaleString()}`} className="text-success" />
                <Row label="Loans" value={`$${Number(s.loans).toLocaleString()}`} />
                <Row label="Work-study" value={`$${Number(s.workStudy).toLocaleString()}`} />
              </dl>
              <div className="mt-4 pt-4 border-t border-border space-y-2">
                <Row label="Net cost / year" value={<span className="font-heading text-2xl text-ink">${s.netCost.toLocaleString()}</span>} />
                <Row label="4-year cost" value={<span className="text-ink">${s.fourYear.toLocaleString()}</span>} />
                <Row label="% of COA that's free money" value={<span className={s.pctFree >= 50 ? "text-success" : "text-amber"}>{s.pctFree}%</span>} />
              </div>
              <button onClick={() => remove(s.id)} className="mt-4 text-xs text-ink-dim hover:text-danger flex items-center gap-1" data-testid={`compare-remove-${s.id}`}>
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            </div>
          ))}
        </div>
      )}

      {schools.length >= 2 && (
        <div className="card mt-6 text-xs text-ink-muted" data-testid="compare-insight">
          Lowest net cost wins, but also compare the "% of COA that's free money" column. A school with more grants and fewer loans is almost always a better deal, even if its headline net cost is similar.
        </div>
      )}
    </ToolShell>
  );
}

function Row({ label, value, className = "" }) {
  return (
    <div className="flex justify-between">
      <dt className="text-ink-muted">{label}</dt>
      <dd className={className || "text-ink"}>{value}</dd>
    </div>
  );
}
